import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  FeatureName,
  useIsFeatureEnabled,
  WorkspaceAsset,
} from '@gripp/shared-logic';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import openMap from 'react-native-open-maps';
import { Text } from 'react-native-paper';
import { useResponsiveDesign } from '../../hooks/useResponsiveDesign';
import { Colors } from '../../themes';
import { Link } from '../crop-circle/Link';
import { ArrowTopRightAngleIcon, LocationIcon } from '../crop-circle/icons';
import { RoutineSubHeader } from './routineSubHeader';

type AssetDetailsProps = {
  workspaceAsset: WorkspaceAsset | undefined;
};

export const AssetDetails = (props: AssetDetailsProps) => {
  const { workspaceAsset } = props;
  const isResponsiveStyle = useResponsiveDesign();
  const isCropCircleRedesignEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  if (!workspaceAsset) {
    return null;
  }

  const { asset } = workspaceAsset;
  const showSubHeader =
    workspaceAsset?.routineState && workspaceAsset.routineState.length > 0;

  // TODO: Crop Circle Redesign | Remove after feature is fully rolled out
  if (!isCropCircleRedesignEnabled) {
    return (
      asset && (
        <>
          <Text
            testID="asset-name"
            style={[
              styles.assetTitle,
              { fontSize: isResponsiveStyle ? 18 : 22 },
            ]}
          >
            {asset.name}
          </Text>
          {asset.lastAddress && (
            <Text style={styles.locationText}>
              {asset.lastAddress}
              <Pressable
                onPress={() =>
                  openMap({
                    query: `${asset.lastLocation.coordinates[1]}, ${asset.lastLocation.coordinates[0]}`,
                  })
                }
              >
                <MaterialCommunityIcons
                  name="open-in-new"
                  color={Colors.primary}
                  size={17}
                  style={styles.locationButton}
                />
              </Pressable>
            </Text>
          )}
          <View
            style={[
              {
                marginTop: 5,
                overflow: 'hidden',
                maxHeight: 50,
                width: '100%',
              },
            ]}
          >
            {showSubHeader && (
              <RoutineSubHeader workspaceAsset={workspaceAsset} />
            )}
          </View>
        </>
      )
    );
  }

  return (
    asset && (
      <View style={styles.containerCropCircle}>
        <View style={styles.subHeaderContainer}>
          {showSubHeader && (
            <RoutineSubHeader workspaceAsset={workspaceAsset} />
          )}
        </View>
        {asset.lastAddress && (
          <View>
            <Link
              children={asset.lastAddress}
              underline={false}
              rightIcon={<ArrowTopRightAngleIcon size={12} />}
              leftIcon={<LocationIcon size={12} />}
              onPress={() =>
                openMap({
                  query: `${asset.lastLocation.coordinates[1]}, ${asset.lastLocation.coordinates[0]}`,
                })
              }
              emphasised={false}
              color="neutral"
              textStyle={{ fontWeight: '500' as const }}
              wrap
            />
          </View>
        )}
      </View>
    )
  );
};

const styles = StyleSheet.create({
  locationButton: {
    paddingLeft: 5,
    top: 3,
  },
  locationText: {
    ...Platform.select({
      web: {
        fontSize: 12,
        fontWeight: '400',
      },
      default: {
        fontSize: 12,
        fontWeight: '500',
      },
    }),
  },
  assetTitle: {
    fontWeight: '700',
  },
  containerCropCircle: {
    flex: 1,
    // Allow this flex column to shrink below its content width (default is
    // minWidth: auto = content size) so the routine-pill row wraps within the
    // available space instead of forcing the column wider and overflowing.
    minWidth: 0,
    display: 'flex',
    justifyContent: 'space-between',
    // Fill the header by default (pins the address to the bottom) but grow past
    // it when the pills wrap onto extra rows. Matches the header's minHeight.
    minHeight: 88,
  },
  subHeaderContainer: {
    width: '100%',
  },
});
